<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { computed, onMounted, watch } from "vue";
import { RouterView, useRouter } from "vue-router";

import { useLoader } from "@/common/composables/use-loader.composable";
import LottieAnimation from "@/components/LottieAnimation.vue";
import ModalManager from "@/components/ModalManager.vue";
import { IframeThemingHelper, MatomoHelper } from "@/helpers";
import AppLayout from "@/layouts/AppLayout.vue";
import { useAuthStore, useSettingsStore } from "@/stores";
import { applyTheme } from "@/theming";

import { isIframe } from "./common/is-iframe";

const router = useRouter();

const { showLoading } = useLoader(router);
const settingsStore = useSettingsStore();
const authStore = useAuthStore();

const theme = computed(() => settingsStore.userPreferences.theme);

const { user } = storeToRefs(authStore);

const setupIframeMessages = () => {
  window.addEventListener("message", (event) => {
    if (!event.data.type?.startsWith("abc:")) {
      return;
    }

    const { type, data } = event.data;

    if (type === "abc:set-colors") {
      // Remove characters that are not digits or spaces
      const { primary } = data.colors as IframeThemingHelper.IframeColors;

      const rootEl = document.querySelector(":root") as HTMLElement;

      if (IframeThemingHelper.isValidColorSet(primary)) {
        const rgbValues = [primary.r, primary.g, primary.b];
        rootEl.style.setProperty("--primary-base-color", rgbValues.join(","));

        const modifyColor = (amount: number) => (color: number) =>
          Math.min(255, Math.max(0, color + amount));

        // Generate remaining colors based on primary color
        rootEl.style.setProperty("--primary-110-color", rgbValues.map(modifyColor(-10)).join(","));
        rootEl.style.setProperty("--primary-50-color", rgbValues.map(modifyColor(50)).join(","));
        rootEl.style.setProperty("--primary-25-color", rgbValues.map(modifyColor(75)).join(","));
        rootEl.style.setProperty("--primary-10-color", rgbValues.map(modifyColor(90)).join(","));
      }
    }
  });

  window.parent.postMessage({ type: "abc:ready" }, "*");
};

watch(
  () => theme.value,
  (newTheme) => {
    applyTheme(newTheme);
  },
  { immediate: true }
);

watch(
  () => user.value,
  (newUser) => {
    if (newUser.function !== "") {
      MatomoHelper.updateUserType([user.value.function]);
    }
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  if (isIframe()) {
    setupIframeMessages();
  }
});
</script>

<template>
  <Transition name="fade" appear>
    <div v-if="!showLoading">
      <AppLayout>
        <RouterView :key="$route.path" class="flex-grow-1" />
      </AppLayout>
      <ModalManager />
    </div>
  </Transition>

  <div v-if="showLoading" class="app-loading">
    <LottieAnimation class="app-loading__animation" path="loading" />
  </div>
</template>
